import JSON5 from "json5";
interface DataNode {
	key: string;
	element: Element;
	children: DataNode[];
}

/**
 * Scans a string for one or more balanced JSON objects ( {...} ) while respecting
 * string literals (so that braces inside quotes are ignored), and returns an array
 * of parsed objects using JSON5. If none are found, returns an empty array.
 */
function parseMultipleJsonObjects(str: string): unknown[] {
	const results: unknown[] = [];
	let i = 0;
	const len = str.length;

	while (i < len) {
		// Find the first '{' that is not inside a string.
		let startIndex = -1;
		let inString = false;
		let stringChar = "";
		let esc = false;
		for (; i < len; i++) {
			const ch = str[i];
			if (inString) {
				if (esc) {
					esc = false;
				} else if (ch === "\\") {
					esc = true;
				} else if (ch === stringChar) {
					inString = false;
				}
			} else {
				if (ch === '"' || ch === "'") {
					inString = true;
					stringChar = ch;
				} else if (ch === "{") {
					startIndex = i;
					break;
				}
			}
		}
		if (startIndex === -1) break; // no more opening brace found

		// Now scan forward from startIndex to find the matching closing brace.
		let braceCount = 0;
		inString = false;
		stringChar = "";
		esc = false;
		let endIndex = -1;
		for (let j = startIndex; j < len; j++) {
			const ch = str[j];
			if (inString) {
				if (esc) {
					esc = false;
				} else if (ch === "\\") {
					esc = true;
				} else if (ch === stringChar) {
					inString = false;
				}
			} else {
				if (ch === '"' || ch === "'") {
					inString = true;
					stringChar = ch;
				} else if (ch === "{") {
					braceCount++;
				} else if (ch === "}") {
					braceCount--;
					if (braceCount === 0) {
						endIndex = j;
						break;
					}
				}
			}
		}
		if (endIndex === -1) break; // no matching closing brace found

		const snippet = str.substring(startIndex, endIndex + 1).trim();
		try {
			const parsed = JSON5.parse(snippet);
			results.push(parsed);
		} catch (err) {
			console.warn("Failed to parse JSON snippet:", snippet, err);
		}
		i = endIndex + 1;
	}

	return results;
}

/**
 * Returns the closest ancestor of `el` that is a <data> element with a "key" attribute.
 */
function findClosestDataAncestor(el: Element): Element | null {
	let parent = el.parentElement;
	while (parent) {
		if (parent.tagName.toUpperCase() === "DATA" && parent.hasAttribute("key")) {
			return parent;
		}
		parent = parent.parentElement;
	}
	return null;
}

/**
 * Builds a tree of DataNode objects from the document.
 * Each <data key="..."> element becomes a node; if its closest data ancestor is also
 * in the tree, it is added as a child; otherwise, it is top-level.
 */
function buildDataTree(doc: Document): DataNode[] {
	const dataEls = Array.from(doc.querySelectorAll("data[key]"));
	const nodeMap = new Map<Element, DataNode>();

	// Create a DataNode for each <data> element.
	for (const el of dataEls) {
		const key = el.getAttribute("key") ?? "";
		nodeMap.set(el, { key, element: el, children: [] });
	}

	const topNodes: DataNode[] = [];
	// Attach each node to its parent (if any).
	for (const [el, node] of nodeMap) {
		const parentDataEl = findClosestDataAncestor(el);
		if (parentDataEl && nodeMap.has(parentDataEl)) {
			nodeMap.get(parentDataEl)?.children.push(node);
		} else {
			topNodes.push(node);
		}
	}
	return topNodes;
}

/**
 * Extracts the direct text from an element (excluding any text from nested <data> elements).
 * We do this by cloning the element and then removing all descendant <data key="..."> nodes.
 */
function extractDirectJSON(el: Element): string {
	const clone = el.cloneNode(true) as HTMLElement;
	for (const child of clone.querySelectorAll("data[key]")) {
		child.remove();
	}
	// clone.querySelectorAll("data[key]").forEach((child) => child.remove());
	return (clone.textContent || "").trim();
}

/**
 * Recursively computes the JSON value for a DataNode:
 * 1. Extract the node’s direct JSON text and parse it (if any).
 * 2. For each child node, compute its value and attach it under its key.
 */
function computeNodeValue(node: DataNode): unknown {
	const directText = extractDirectJSON(node.element);
	const parsed = parseMultipleJsonObjects(directText);
	let value: unknown;
	if (parsed.length === 1) {
		value = parsed[0];
	} else if (parsed.length > 1) {
		value = parsed;
	} else {
		value = {};
	}
	if (typeof value !== "object" || value === null) {
		value = {};
	}
	const objValue = value as Record<string, unknown>;
	// Attach children under their keys.
	for (const child of node.children) {
		objValue[child.key] = computeNodeValue(child);
	}
	return value;
}

/**
 * Parses an HTML fragment into nested JSON.
 * Only top-level <data> elements (those without a data parent) are added at the root,
 * and their nested <data> elements become nested properties.
 */
export default function parseHtmlFragment(
	html: string,
): Record<string, unknown> {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, "text/html");
	const tree = buildDataTree(doc);
	const result: Record<string, unknown> = {};
	for (const node of tree) {
		result[node.key] = computeNodeValue(node);
	}
	return result;
}
